import { useEffect, useMemo, useRef, useState, Fragment, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, navigate, Link } from "@reach/router";
import { useQueryClient } from "react-query";
import { ReactComponent as IconArrowLeftShort } from 'bootstrap-icons/icons/arrow-left-short.svg'
import { ReactComponent as IconArrowRightShort } from 'bootstrap-icons/icons/arrow-right-short.svg'
import { ReactComponent as IconSquare } from 'bootstrap-icons/icons/square.svg'
import { ReactComponent as IconCheckSquare } from 'bootstrap-icons/icons/check-square.svg'
import { ReactComponent as IconPrinter } from 'bootstrap-icons/icons/printer.svg'
import { ReactComponent as IconPrescription } from 'bootstrap-icons/icons/prescription.svg'
import { ReactComponent as IconEye } from 'bootstrap-icons/icons/eye.svg'

import deburr from 'lodash/deburr'
import uniqBy from 'lodash/uniqBy'

import parseDate from 'lib/parseDate'
import useQuery, { request as apiRequest } from "hooks/useQuery";
import getAccessToken from "hooks/getAccessToken";
import useBetaFeatures from "hooks/useBetaFeatures";
import { useGrowth } from "hooks/usePercentilesApi";
import useMutation from "hooks/useMutation";
import useUser from "hooks/useUser";
import useTeamID from "hooks/useTeamID";
import useRecordSavedListener from "hooks/useRecordSavedListener";
import useStaleDataToast from "hooks/useStaleDataToast";
import useAiSummaryPending from "hooks/useAiSummaryPending";
import useFeatureAccess from "hooks/useFeatureAccess";
import TermsLockModal from "Components/TermsLockModal/TermsLockModal";
import Timer from "Components/Timer/Timer"

import styles from './styles.module.scss'
import { formatDate } from 'config/i18n'
import { getBMI, getClCr } from './helpers'
import Consumer from './Consumer'
import Consultations from './Consultations'
import Tables from './Tables'
import Table from './Table'
import CustomForms from './CustomForms/CustomForms'
import { useConsumerFormRecordsCount } from './CustomForms/hooks'
import Filters from './Filters'
import Form from './Form/Form'
import EHRVisibilityModal from './EHRVisibilityModal'
import Notes from './Notes/Notes'
import HceSummary from './HceSummary/HceSummary'
import { TrialInfoPopover } from 'Components/TrialInfoPopover/TrialInfoPopover'
import { BetaFeedback } from 'Components/BetaFeedback/BetaFeedback'
import EscribaButton from './Escriba/EscribaButton'
import EscribaPending from './Escriba/EscribaPending'
import { useEscribaApi } from 'hooks/useEscribaApi'
import { usePlanLock, LockedLabel } from 'Components/FeatureBloqueada/PlanLock'

// Trigger de una feature de la HCE bloqueada por plan: misma pill que las trial
// tools (bg blanco, rounded-3, padding 4), texto atenuado y candado a color pleno
// (Figma 3352:26753). Sin TrialInfoPopover ni feedback: no hay feature que probar.
const PlanLockedTool = ({ featureKey, label, color, onClick, className = '' }) => (
  <div className={`d-flex align-items-center gap-1 rounded-3 bg-white ${className}`} style={{ padding: 4 }}>
    <button
      type="button"
      data-testid={`plan-locked-${featureKey}`}
      className="d-flex align-items-center gap-1 btn p-0 border-0 font-nunito"
      style={{ color }}
      onClick={onClick}
    >
      <LockedLabel blocked iconClassName="">{label}</LockedLabel>
    </button>
  </div>
)

// lazy (patrón Consultations.js:76): el odontograma es el componente más
// grande de la HCE y lo usa una fracción chica de los teams — sin esto
// entraría al bundle principal del 100% de los usuarios aunque tengan la
// feature apagada.
const Odontograma = lazy(() => import('./Odontograma/Odontograma'))
// Mismo criterio para percentiles: solo lo usan teams con la beta prendida,
// y el chart SVG + tablas no tienen por qué viajar en el bundle de todos.
const Percentiles = lazy(() => import('./Percentiles/Percentiles'))
// Mismo criterio: MediaRecorder + la subida por partes no tienen por qué
// viajar en el bundle de todos. EscribaButton.js es eager (chico, sin
// MediaRecorder) — sólo Escriba.js (el orquestador real) es lazy.
const Escriba = lazy(() => import('./Escriba/Escriba'))
// Odontograma+Percentiles para "Imprimir HCE" (pedido de Nacho) — mismo
// criterio de arriba: GrowthChart/OdontogramaChart/OdontogramaList no
// tienen por qué viajar en el bundle de todos. `importPrintExtras` (la
// MISMA función, no una redefinida) se usa también en el click de
// "Imprimir" para poder esperar (await) el chunk ANTES de imprimir — un
// Suspense boundary no se puede "esperar" desde un handler imperativo.
const importPrintExtras = () => import('./PrintExtras')
const PrintExtras = lazy(importPrintExtras)

const clone = window.structuredClone || JSON.clone
let V2_DATE = new Date('2023-08-22T09:00').getTime()
let V2_CLOSE = 3 * 60 * 60 * 1000

const LABS_GROUPS = [
  'hemograma',
  'ionograma',
  'eabrenal',
  'hepatograma',
  'perfiltiroideo',
  'perfillipidico',
  'perfilferrico',
  'diabetes'
]
const VITAL_SIGNS_VARIABLES = ['tas', 'tad', 'fc', 'fr', 'temp', 'sat', 'fio2', 'height', 'weight', 'abdperim', 'headperim']
const FALSY = [undefined, "", null]
// Ventana de edición de registros de la HCE (evolutions/diagnostics/
// treatments/vitalSigns) — hoisteado a módulo (antes vivía inline donde se
// arma `items`), nombrado en vez de un número mágico repetido.
const EDITABLE_WINDOW_MS = 48 * 60 * 60 * 1000

const sort = (key, order) => (a, b) => {
  let aDate = new Date(a[key] || a.date || a.startsAt || a.createdAt).getTime()
  let bDate = new Date(b[key] || b.date || b.startsAt || b.createdAt).getTime()
  if (order === 'desc') {
    if (aDate > bDate) return -1
    if (aDate < bDate) return 1
  }
  if (aDate < bDate) return -1
  if (aDate > bDate) return 1
  return 0
}

function EHR({ consumerID }) {
  const location = useLocation();
  const { t } = useTranslation();
  const prevConsumerIDRef = useRef(consumerID);
  const [eventID, setEventID] = useState();
  const [stats, setStats] = useState({})
  const [tables, setTables] = useState()
  const [files, setFiles] = useState()
  const [consultations, setConsultations] = useState()
  const [ready, setReady] = useState()
  const [showNotes, setShowNotes] = useState(false);
  const [showHceSummary, setShowHceSummary] = useState(false);
  const [showOdontograma, setShowOdontograma] = useState(false);
  const [showPercentiles, setShowPercentiles] = useState(false);
  const [showEscriba, setShowEscriba] = useState(false);
  const escribaFeedbackRef = useRef(null);
  const hceSummaryFeedbackRef = useRef(null);
  const odontogramaFeedbackRef = useRef(null);
  const percentilesFeedbackRef = useRef(null);

  // Fix responsive (2026-08-14): reportado por Nacho — sidebar (main,
  // Consultations) y el panel de notas/resumen IA (notesWdith) son 400px
  // FIJOS cada uno, en una fila sin flex-wrap junto al contenido principal
  // — en mobile (y peor si los 3 están abiertos a la vez, son independientes
  // entre sí) esto por sí solo ya fuerza la página entera más ancha que el
  // viewport, sin necesitar la barra de filtros (arreglada aparte) para
  // romperse. Debajo de lg (992px, mismo breakpoint que el sidebar de
  // Agenda) los 3 paneles pasan a apilarse a ancho completo en vez de
  // convivir lado a lado.
  const [compact, setCompact] = useState(() => window.innerWidth < 992)
  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < 992)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const onRecordUpdate = (updatedRecord) => {
    // Actualizar newRecords para sincronizar con Tables
    addRecords(prevRecords => {
      const array = [...prevRecords]
      let index = array.findIndex(r => r.id === updatedRecord.id)
      if (index >= 0) {
        array[index] = { ...updatedRecord }
      } else {
        array.push({ ...updatedRecord })
      }
      return array
    })

    // Actualizar consultations
    setConsultations(prevConsultations => {
      if (!prevConsultations || !updatedRecord.type) return prevConsultations
      
      const newConsultations = { ...prevConsultations }
      
      // Bug real (Juan Cruz, card Percentiles 23/09): consultations[group]
      // guarda evolutions/diagnostics/treatments/files como ARRAYS (ver el
      // ||= más abajo, en el useEffect que arma consultations/tables), pero
      // vitalSigns/labs/forms son CONTADORES (number, ej. `vitalSigns: 0`)
      // — el mismo groupKey nunca tiene una lista real ahí. Antes, apenas
      // ese contador pasaba de 0 a un valor real (tras el primer guardado,
      // vía el rebuild de newRecords), la guarda de arriba (`!consultation
      // [targetSection]`) dejaba de frenar porque un number != 0 es
      // truthy, y `.findIndex` sobre un number tiraba
      // "t[r].findIndex is not a function" — reproducible desde
      // Percentiles.js recién a partir de PR #367 (primera vez que algo le
      // manda un record type:'vitalSigns' a onRecordUpdate), pero la
      // guarda ya estaba mal para CUALQUIER tipo contador, no solo
      // vitalSigns. Estos 3 tipos no necesitan parche puntual acá: el
      // mismo rebuild que ya corre por `newRecords` (ver addRecords arriba)
      // recalcula sus contadores/values solo, con la próxima pasada.
      const typeToSection = {
        'records': 'evolutions',
        'diagnostics': 'diagnostics',
        'treatments': 'treatments',
        'files': 'files',
      }

      const targetSection = typeToSection[updatedRecord.type]
      if (!targetSection) return newConsultations

      const recordDate = new Date(updatedRecord.createdAt || updatedRecord.date || updatedRecord.updatedAt)
      const recordDateStr = recordDate.toLocaleDateString('fr-ca')
      const recordCreatedBy = updatedRecord.createdBy || updatedRecord.createdByName
      
      const possibleGroupKeys = []
      if (recordCreatedBy) {
        possibleGroupKeys.push(`${recordCreatedBy}:${recordDateStr}`)
      }
      
      const allGroupKeys = Object.keys(newConsultations).filter(key => key !== 'groups')
      
      for (const groupKey of [...possibleGroupKeys, ...allGroupKeys]) {
        if (groupKey === 'groups') continue
        
        const consultation = newConsultations[groupKey]
        if (!consultation || !consultation[targetSection]) continue

        const recordIndex = consultation[targetSection].findIndex(r => r.id === updatedRecord.id)
        if (recordIndex !== -1) {
          newConsultations[groupKey] = {
            ...consultation,
            [targetSection]: [
              ...consultation[targetSection].slice(0, recordIndex),
              updatedRecord,
              ...consultation[targetSection].slice(recordIndex + 1)
            ]
          }
          break
        }
      }
      
      return newConsultations
    })
  }
  const [newRecords, addRecords] = useState([])
  const [closedAt, setClosedAt] = useState(null)
  const [prescriptions, setPrescriptions] = useState()
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)
  const [resourceEhrConfig, setResourceEhrConfig] = useState()
  const [originalResourceEhrConfig, setOriginalResourceEhrConfig] = useState()

  const event = useQuery(window._l(`/events/${eventID}`).substr(1), {
    enabled: !!eventID,
  });

  const [saveEvent] = useMutation(window._l(`/events/${eventID}`).substr(1), {
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  let qPrescriptions = useQuery(`prescriptions/consumers/${consumerID}`, {
    onSuccess: data => {
      if (!data) return
      let payload = clone(data)
      payload._form = !!prescriptions?._form
      setPrescriptions(payload)
    },
    retry: false
  })

  const [filters, setFilters] = useState()
  const [timer, setTimer] = useState(null)

  // Fix (auditoría 2026-08-14, hallazgo CRÍTICO): la ruta reusa esta MISMA
  // instancia de EHR al cambiar de paciente (@reach/router no remonta por
  // cambio de param) — sin este reset, el estado clínico ya cargado/editado
  // de un paciente (newRecords, prescriptions, tables, consultations, etc.)
  // quedaba pegado al abrir OTRO paciente, mezclando datos clínicos entre
  // historias. Se resetea de forma SÍNCRONA durante el render (no en un
  // efecto) para que React descarte el render intermedio antes de pintarlo
  // — un useEffect llegaría un frame tarde y el usuario alcanzaría a ver el
  // resumen del paciente anterior.
  if (prevConsumerIDRef.current !== consumerID) {
    prevConsumerIDRef.current = consumerID
    setReady(false)
    setEventID(undefined)
    setStats({})
    setTables(undefined)
    setFiles(undefined)
    setConsultations(undefined)
    addRecords([])
    setPrescriptions(undefined)
    setFilters(undefined)
  }

  const user = useUser();
  const teamID = useTeamID();

  const consumer = useQuery(window._l(`/consumers/${consumerID}`).substr(1));

  // Acceso a las herramientas de la HCE en UN request (/plans/team/:id/access):
  // el back ya resuelve plan, activación por team, especialidad y T&C, con el
  // enforcement de planes prendido o apagado. Por herramienta:
  //   - <x>Enabled: se muestra como herramienta propia (no visible o bloqueada
  //     por plan → no). Gatea también el subtree (badge, modal lazy, datos).
  //   - <x>TermsLocked: se muestra, pero al clickear pide los T&C.
  //   - <x>PlanLocked: candado de plan, sólo si la especialidad la deja ver.
  // Mientras carga no se muestra ninguna; si falla, todas sin candado (el api
  // corta igual). Ver hooks/useFeatureAccess.js.
  const featureAccess = useFeatureAccess();
  const hceSummaryAccess = featureAccess.access('hce.ai_summary');
  const odontogramaAccess = featureAccess.access('hce.odontograma');
  const percentilesAccess = featureAccess.access('hce.percentiles');
  const escribaAccess = featureAccess.access('escriba.recording');
  const isEnabled = a => a.visible && a.blockedBy !== 'plan';
  const isTermsLocked = a => a.blockedBy === 'terms';
  const isPlanLocked = a => a.visible && a.blockedBy === 'plan';
  const hceSummaryEnabled = isEnabled(hceSummaryAccess);
  const odontogramaEnabled = isEnabled(odontogramaAccess);
  const percentilesEnabled = isEnabled(percentilesAccess);
  const escribaEnabled = isEnabled(escribaAccess);
  const hceSummaryTermsLocked = isTermsLocked(hceSummaryAccess);
  const odontogramaTermsLocked = isTermsLocked(odontogramaAccess);
  const percentilesTermsLocked = isTermsLocked(percentilesAccess);
  const escribaTermsLocked = isTermsLocked(escribaAccess);
  const hceSummaryPlanLocked = isPlanLocked(hceSummaryAccess);
  const odontogramaPlanLocked = isPlanLocked(odontogramaAccess);
  const percentilesPlanLocked = isPlanLocked(percentilesAccess);
  const escribaPlanLocked = isPlanLocked(escribaAccess);
  // La voz es un sub-flag del team y no mira la especialidad ni los T&C: sólo
  // vale con el odontograma usable.
  const odontogramaVoiceEnabled = odontogramaAccess.usable && odontogramaAccess.voice;
  const [showTermsLock, setShowTermsLock] = useState(false);
  // ARCA todavía sale de /features (pasa a /access en el próximo tramo). No
  // suma un request: App.js ya pide /features por los flags de la UI web.
  const betaFeatures = useBetaFeatures();
  // `enabled` explícito: el acceso ya se resolvió arriba vía /access, así
  // que esta instancia no necesita pedir /escriba/enabled otra vez (mismo
  // criterio de performance que hce_summary — reunión del 19/08).
  const escribaApi = useEscribaApi({ enabled: escribaEnabled });

  // Modal de "Disponible en …" de cada herramienta bloqueada por plan. Cuándo
  // se muestra el candado lo deciden los <x>PlanLocked de arriba.
  const hceSummaryLock = usePlanLock('hce.ai_summary');
  const odontogramaLock = usePlanLock('hce.odontograma');
  const percentilesLock = usePlanLock('hce.percentiles');
  const escribaLock = usePlanLock('escriba.recording');

  // Alertas de crecimiento en el botón de la toolbar: sin esto, un cruce de
  // ≥2 carriles de percentil solo se ve si el profesional ABRE el modal — el
  // screening pasivo es el punto de la feature. El fetch está triplemente
  // gateado (flag del team + paciente PEDIÁTRICO por dob + consumer ya
  // cargado) para no pagar el costo de records() en cada HCE de adultos; la
  // query key es la MISMA que usa el modal, así react-query dedupea y abrir
  // el modal no dispara un request extra. Sin useEffect nuevo (regla D8 de
  // esta pantalla): todo derivado en render.
  // MISMO criterio de parseo que el backend (parseDobMs, api): epoch
  // numérico (consumers importados traen dob como string numérico) o
  // "YYYY-MM-DD" ESTRICTO. Nada de new Date(dobRaw) como fallback: acepta
  // formatos ambiguos ('10/01/2022' con mes/día invertidos según el motor)
  // que el backend rechaza — el badge habilitaría la query, el server
  // respondería dob_required, y quedaría un request inútil por apertura con
  // badge y modal en desacuerdo silencioso.
  const dobRaw = consumer.data?.dob
  const dobIsoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dobRaw ?? ''))
  const dobMsForBadge = dobRaw == null || dobRaw === ''
    ? null
    : Number.isFinite(Number(dobRaw))
      ? Number(dobRaw)
      : dobIsoMatch ? Date.UTC(Number(dobIsoMatch[1]), Number(dobIsoMatch[2]) - 1, Number(dobIsoMatch[3])) : null
  const consumerAgeYears = Number.isFinite(dobMsForBadge)
    ? (Date.now() - dobMsForBadge) / (365.25 * 24 * 60 * 60 * 1000)
    : null
  const isPediatric = consumerAgeYears != null && consumerAgeYears >= 0 && consumerAgeYears < 19
  // Ninguna tabla OMS de este indicador cubre adultos (weight hasta ~10a,
  // height/bmi hasta ~19a, headc hasta ~5a — api/src/percentiles/lms.js) —
  // en un paciente adulto CON dob conocido, el server devuelve las 4 curvas
  // vacías (router.js) y encima cuenta como "descartada" cada medición de
  // peso/talla que el paciente tenga cargada como adulto (fuera de rango
  // por EDAD, no por valor implausible): el modal mostraba el mensaje "las
  // curvas se muestran igual" sin dibujar ningún gráfico, más una alerta
  // amarilla de descartes que no describe un problema real de datos. Sin
  // dob conocido NO se puede afirmar que el paciente no es pediátrico — ahí
  // se deja el botón (el modal ya tiene el mensaje dedicado `dob_required`
  // que pide completarlo, útil para el profesional).
  const percentilesApplicable = isPediatric || dobMsForBadge == null
  const growthForAlerts = useGrowth(consumerID, percentilesEnabled && isPediatric)
  const percentilesAlertCount = growthForAlerts.data?.alerts?.length || 0
  // El refetch post-guardado va DEMORADO (no inmediato): el backend escribe
  // async (records/save.js retorna y encola compute con delay de 150ms, que
  // recién ahí borra cache/{id}/records; el insert a PG va después por otra
  // cola) — un refetch inmediato lee los datos PRE-guardado, los marca
  // frescos por 30s (staleTime) y el modal (misma query key) muestra el
  // gráfico SIN el punto recién cargado; peor, puede re-cachear server-side
  // la lista vieja por 24h si cae entre el borrado del cache y el insert.
  // El ref + cleanup evita un refetch sobre una HCE ya desmontada.
  const growthRefetchTimer = useRef(null)
  const growthRefetchRef = useRef(() => {})
  growthRefetchRef.current = () => growthForAlerts.refetch()
  useEffect(() => () => clearTimeout(growthRefetchTimer.current), [])

  // Imprimir HCE + Odontograma (pedido de Nacho, feature nueva): a
  // diferencia de percentiles (growthForAlerts arriba, ya se fetchea sin
  // condición extra para el badge de alertas), odontograma NO tiene un
  // fetch equivalente a propósito — useOdontogramaChart documenta que su
  // query debe montar/desmontar CON el modal (pollea cada 60s para
  // detectar ediciones de un colega sobre el mismo documento compartido) —
  // llamarlo sin condición acá agregaría ese polling permanente para
  // cualquier team con la feature prendida, imprima o no. En cambio: se
  // fetchea UNA vez, on-demand, recién al clickear "Imprimir" — misma key
  // que usa el modal (cachea junto, instantáneo si ya se abrió esta
  // sesión).
  const odontogramaChartKeyForPrint = window._l(`/odontograma/charts/${consumerID}`).substr(1)
  const odontogramaCatalogKeyForPrint = window._l('/odontograma/catalog').substr(1)
  const [printExtrasReady, setPrintExtrasReady] = useState(false)
  const [printBusy, setPrintBusy] = useState(false)
  const odontogramaChartForPrint = useQuery(odontogramaChartKeyForPrint, { enabled: printExtrasReady && odontogramaEnabled })
  const odontogramaCatalogForPrint = useQuery(odontogramaCatalogKeyForPrint, { enabled: printExtrasReady && odontogramaEnabled })
  const queryClient = useQueryClient()
  const accessTokenForPrint = getAccessToken()

  const onPrintClick = async () => {
    // #print (global, usado por el flujo de impresión INTERNO de
    // Odontograma — ver Odontograma.js#printing) puede quedar con
    // contenido viejo si su cleanup de `afterprint` todavía no corrió —
    // index.scss oculta TODO #root mientras #print no esté vacío, así que
    // sin este clear acá "Imprimir" imprimiría la página entera en blanco.
    document.getElementById('print')?.replaceChildren()
    if (!odontogramaEnabled) { window.print(); return }
    setPrintBusy(true)
    await Promise.all([
      importPrintExtras(),
      queryClient.fetchQuery([odontogramaChartKeyForPrint], () => apiRequest(odontogramaChartKeyForPrint, accessTokenForPrint.value)),
      queryClient.fetchQuery([odontogramaCatalogKeyForPrint], () => apiRequest(odontogramaCatalogKeyForPrint, accessTokenForPrint.value)),
      // fail-open: si el fetch de odontograma se cae, se imprime igual sin
      // esa sección — no tiene sentido bloquear el resto de la HCE por eso.
    ]).catch(() => {})
    setPrintExtrasReady(true)
    setPrintBusy(false)
    window.print()
  }

  const resource = useQuery(
    window._l(`/${user.team?.resourceID}`).substr(1),
    { enabled: !!user.team?.resourceID }
  );

  const [saveResource] = useMutation(window._l(`/${resource.data?.id}`).substr(1), {
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  const [saveResourceEhrConfig] = useMutation(window._l(`/${resource.data?.id}/ehr-config`).substr(1), {
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
    onSuccess: (data) => {
      setShowVisibilityModal(false);
      setOriginalResourceEhrConfig(data.ehrConfig);
    },
  });

  const forms = useQuery(window._l(`/forms`).substr(1), {
    enabled: !!consumerID,
  });

  // Solo para el badge de conteo del toggle "Formularios" (independiente de si el panel está abierto).
  const customFormRecords = useConsumerFormRecordsCount(consumerID)
  // Remonta <CustomForms> tras migrar un form v1: sus queries (useCustomForms/
  // useMyForms) viven adentro del panel y el remount es la forma más simple de
  // refrescarlas desde acá sin acoplar keys de react-query.
  // Formularios v1 ya migrados a Custom Forms v2 — criterio ÚNICO compartido por el
  // badge de stats y el armado de tablas (dos copias divergentes contaban distinto).
  const migratedLegacyFormIDs = useMemo(
    () => new Set((forms.data || []).filter(f => f.migratedToFormID).map(f => f.id)),
    [forms.data]
  )

  const records = useQuery(
    window._l(`/consumers/${consumerID}/records/_all`).substr(1),
    {
      enabled: !!user.state?.isStaff || !!resource.data?.id
    }
  );

  // Hallazgo de code review 2026-07-30 (10ma pasada): esta vista disparaba el
  // refetch en vivo (useRecordSavedListener) pero no tenía NINGÚN manejo de
  // su resultado — a diferencia de EHR.js (v1), un refetch fallido acá
  // quedaba completamente en silencio, sin avisar al profesional que puede
  // estar viendo datos desactualizados. useStaleDataToast (compartido con
  // v1) cubre esto observando el estado de `records`, sin importar si el
  // refetch lo disparó este listener o react-query por su cuenta.
  useStaleDataToast(consumerID, records);
  useRecordSavedListener(consumerID, records.refetch);

  // Indicador de "procesando" del resumen de videoconsulta con IA (feedback
  // de reunión 2026-08-19) — ver Consultations.js para el placeholder que
  // consume `aiSummaryPending`. Apenas deja de estar pendiente (el pipeline
  // terminó, para bien o para mal) se refresca `records` UNA vez, para que
  // la evolución nueva aparezca sin que el profesional tenga que refrescar
  // la página a mano.
  const { pending: aiSummaryPending, outcome: aiSummaryOutcome } = useAiSummaryPending({ consumerID });
  const wasAiSummaryPendingRef = useRef(false);
  useEffect(() => {
    if (wasAiSummaryPendingRef.current && !aiSummaryPending) records.refetch();
    wasAiSummaryPendingRef.current = aiSummaryPending;
  }, [aiSummaryPending]);

  const endConsultation = () => {
    if (!event.data || !event.data.id) return;
    if (event.data.status !== "active") {
      (window.navigate || navigate)(window._l("/events/agenda"))
      return
    };
    const payload = clone(event.data);
    payload.states = payload.states || [];
    payload.states.push({ createdAt: Date.now(), status: "fulfilled" });
    payload.status = "fulfilled";
    saveEvent(payload);
    (window.navigate || navigate)(window._l("/events/agenda"));
  }

  const closeV2Message = () => {
    let resourceID = resource.data?.id
    if (resourceID) {
      let key = `${resourceID}:ehr:v2:closedAt`
      window.localStorage.setItem(key, Date.now())
      setClosedAt(Date.now())
    }
  }

  const setDefault = () => {
    let resourceID = resource.data?.id
    if (resourceID) {
      let payload = clone(resource.data)
      payload.metadata ||= {}
      payload.metadata.ehr ||= {}
      payload.metadata.ehr.version = 'v2:hard'
      saveResource(payload)
      setClosedAt(Date.now() + (30 * 24 * 60 * 60 * 1000))
    }
  }

  const handleVisibilityChange = (newVisibility) => {
    setResourceEhrConfig(prev => ({
      ...prev,
      visibility: newVisibility
    }))
  }

  const handleOpenVisibilityModal = () => {
    setResourceEhrConfig(originalResourceEhrConfig)
    setShowVisibilityModal(true)
  }

  const saveEhrConfig = () => {
    const ehrConfig = {
      ...resourceEhrConfig,
      visibility: resourceEhrConfig.visibility
    }
    saveResourceEhrConfig(ehrConfig)
  }

  useEffect(() => {
    let resourceID = resource.data?.id
    if (resourceID) {
      let key = `${resourceID}:ehr:v2:closedAt`
      let closedAt = Number(window.localStorage.getItem(key)) || 1
      setClosedAt(closedAt)
      setResourceEhrConfig(resource.data.ehrConfig)
      setOriginalResourceEhrConfig(resource.data.ehrConfig)
    }
  }, [resource.data])

  useEffect(() => {
    let eventID = new URLSearchParams(location.search).get("eventID");
    if (eventID) setEventID(eventID);
  }, [location.search])

  useEffect(() => {
    if (!records.isFetched || !qPrescriptions.isFetched) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'hce_abierta',
      team_id: teamID,
      has_previous_records:
        (Array.isArray(records.data) && records.data.length > 0) ||
        (qPrescriptions.data?.total > 0),
    });
  }, [records.isFetched, qPrescriptions.isFetched]);

  useEffect(() => {
    if (Array.isArray(records.data) || Array.isArray(newRecords)) {
      let stats = {}
      let payload = {
        records: true,
        ...filters,
        deleted: { count: 0, flag: false, ...filters?.deleted },
        createdBy: { ...filters?.createdBy }
      }

      delete payload.createdBy._ids

      // Bug real (auditoría 2026-08-14): newRecords son records recién
      // guardados que quedan acá hasta que algo los limpie — pero nada los
      // limpia cuando records.refetch() (useRecordSavedListener) ya los
      // trajo de vuelta en records.data. Sin dedup por id, un mismo record
      // se contaba DOS veces en stats/badges (mismo criterio de dedup que
      // ya usa el efecto de más abajo al armar tables/consultations).
      let items = [...(records.data || [])]
      for (const item of newRecords) {
        const index = items.findIndex(i => i.id === item.id)
        if (index < 0) {
          items.push(item)
        } else {
          items[index] = item
        }
      }

      for (const record of items) {
        // Migrados a Custom Forms v2: se cuentan por customFormRecords (sección nueva).
        if (record.type === 'forms' && migratedLegacyFormIDs.has(record.formID)) continue
        const key = record.createdBy

        payload.createdBy[key] ||= { count: 0, flag: true, id: record.createdBy }
        payload.createdBy[key].count += 1
        payload.createdBy[key].label ||= record.createdByName || record.createdBy
        if (payload.createdBy[key].label === record.createdBy && record.createdByName) {
          payload.createdBy[key].label = record.createdByName
        }
        if (record.deleted) {
          payload.deleted.count += 1
        }
        stats[record.type] ||= 0
        stats[record.type] += 1
        if (payload[record.type] === undefined) {
          payload[record.type] = true
        }
      }

      payload.createdBy._ids = Object.keys(payload.createdBy)
      setFilters(payload)
      setStats(stats)
    }
  }, [records.data, newRecords, migratedLegacyFormIDs])

  useEffect(() => {
    if (user.state?.uid && filters && Array.isArray(records.data) && Array.isArray(forms.data) && consumer.data?.id) {
      const today = new Date()
      let consultations = {}
      let files = []
      let tables = {
        treatments: {
          values: {}, headers: {}, records: {}, ids: [], setup: {
            orientation: 'horizontal', allNumbers: false, borderless: false
          },
        },
        diagnostics: {
          values: {}, headers: {}, records: {}, ids: [], setup: {
            orientation: 'horizontal', allNumbers: false, borderless: false,
          }
        },
        labs: {
          groups: LABS_GROUPS, ids: [], values: {}, headers: {}, records: {}, setup: {
            orientation: 'vertical', allNumbers: true, borderless: true
          }
        },
        vitalSigns: {
          values: {}, headers: {}, records: {}, setup: {
            orientation: 'vertical', allNumbers: true, borderless: true,
          }, ids: []
        },
        forms: {}
      }

      const div = document.createElement('div')
      let items = clone(records.data || [])

      for (let item of newRecords) {
        let index = items.findIndex(i => i.id === item.id)
        if (index < 0) {
          items.push({ ...item })
        } else if (index >= 0 && item.updatedAt >= items[index].updatedAt) {
          items[index] = { ...item }
        }
      }

      items = items.sort(sort(filters.sortBy || 'date', filters.sort || 'desc'))
      // Pedido de QA-Cormos (card "Mejoras de UX en Percentiles HCE",
      // punto 5): "los parámetros sin completar aparecen igual como ítems
      // vacíos en la historia clínica" — generaliza el gate que antes
      // existía SOLO para headperim a las 11 variables de Signos Vitales.
      // Una variable entra a la tabla si ALGÚN record de este paciente
      // trae un valor real — sin esto, un consultorio que solo carga
      // peso/talla igual veía filas de FiO2/Sat/TAS/TAD llenas de "·" que
      // nunca usa. record[name] puede venir guardado como number (mismo
      // bug que labs/vitalSigns más abajo) — coaccionar a string antes de
      // trimear. Decidido ANTES del loop (no adentro): crear una columna a
      // mitad de la iteración desalinearía sus items contra el resto.
      // headperim mantiene su caso especial (mismo criterio de antes): se
      // muestra igual con el flag de percentiles prendido, aunque nunca se
      // haya cargado — es el único campo consumido por otra feature más
      // allá de Signos Vitales en sí.
      const vitalSignsVariables = VITAL_SIGNS_VARIABLES.filter(name => {
        if (name === 'headperim' && percentilesEnabled) return true
        return items.some(record => record.type === 'vitalSigns' && !FALSY.includes(String(record[name] ?? '').trim()))
      })
      let EHR_EDITABLE_TIME_LIMIT = Date.now() - EDITABLE_WINDOW_MS
      for (const record of items) {
        if (record.type === 'records' && !record.content?.trim()) continue
        const createdBy = record.createdBy || record.createdByName || 'unknown'
        const deleted = !!record.deleted
        if (!filters.createdBy?.[createdBy]?.flag) continue
        if (!filters.deleted.flag && deleted) continue
        let editable = !record.createdAt || record.createdAt >= EHR_EDITABLE_TIME_LIMIT
        editable = editable && (!record.createdBy || record.createdBy === user.state.uid)

        let date = parseDate(record[filters.sortBy] || record.date || record.createdAt || Date.now(), '10:00')
        date ||= parseDate(record.createdAt || Date.now(), '10:00')
        date ||= new Date()
        const group = `${createdBy}:${date.toLocaleDateString('fr-ca')}`
        consultations[group] ||= {
          evolutions: [], diagnostics: [], treatments: [], files: [], vitalSigns: 0, labs: 0, forms: 0,
          date,
          createdBy: record.createdByName || record.createdBy
        }
        if (consultations[group].createdBy && record.createdByName && consultations[group].createdBy !== record.createdByName) {
          consultations[group].createdBy = record.createdByName
        }

        if (record.type === 'records') {
          div.innerHTML = `<div>${record.content ?? ''}</div>`
          const text = deburr(div.textContent?.trim())
          consultations[group].evolutions.push({ ...record, text })
        } else if (record.type === 'files') {
          files.push({ ...record })
          consultations[group].files.push({ ...record })
        } else if (record.type === 'treatments') {
          const id = record.id
          tables.treatments.records[id] = record
          tables.treatments.ids.push(id)
          record.date ||= record.startsAt || record.createdAt
          record.createdAt ||= record.date

          tables.treatments.headers.date ||= { items: [] }
          let date = parseDate(record[filters.sortBy] || record.date, '10:00')

          tables.treatments.headers.date.items.push({ id, value: date, editable })

          let { drug, company, product, dose, presentation, frecuency, notes, startsAt, endsAt, status } = record
          tables.treatments.values.product ||= { items: [], id: 'product' }
          tables.treatments.values.dose ||= { items: [], id: 'dose' }
          tables.treatments.values.frecuency ||= { items: [], id: 'frecuency' }
          tables.treatments.values.notes ||= { items: [], id: 'notes' }
          tables.treatments.values.status ||= { items: [], id: 'status' }

          product ||= drug || record.label
          if (company) {
            product = `<div>${product} <sup class="text-secondary">${company}</sup></div>`
          }
          if (presentation) {
            product += `<div class="text-xs text-secondary">${presentation}</div>`
          }
          tables.treatments.values.product.items.push({ id, value: product, message: drug !== product ? drug : undefined })
          if (dose && dose !== presentation) {
            tables.treatments.values.dose.items.push({ id, value: dose })
          } else {
            tables.treatments.values.dose.items.push({ id, value: '' })
          }

          tables.treatments.values.frecuency.items.push({ id, value: frecuency })
          tables.treatments.values.notes.items.push({ id, value: notes })

          status = {
            value: status,
            label: t(`ehr:${status}`, { context: 'treatments' })
          }

          let dates = []

          try {
            if (startsAt) {
              dates.push(formatDate(startsAt, 'dd-MMM-yyyy').toUpperCase())
            } else {
              dates.push(t('ehr:startsAt_not_defined', '(n/a)', { context: 'treatments' }))
            }

            if (endsAt) {
              dates.push(formatDate(endsAt, 'dd-MMM-yyyy').toUpperCase())
            } else {
              dates.push(t('ehr:endsAt_not_defined', '(n/a)', { context: 'treatments' }))
            }
          } catch {}

          status.content = status.label
          if (status.value === 'active') {
            status.content = `<div class="text-success">${status.label}</div>`
          } else if (status.value === 'done') {
            status.content = `<div class="text-primary">${status.label}</div>`
          } else if (status. value === 'pending') {
            status.content = `<div class="text-warning">${status.label}</div>`
          }

          status.content += `<div class="text-xs text-secondary">${dates.join(' · ')}</div>`


          tables.treatments.values.status.items.push({ id, value: status.content })

          consultations[group].treatments.push({ ...record, product, status: status.content })
        } else if (record.type === 'diagnostics') {
          let { dx, label, notes, startsAt, status, endsAt } = record
          label ||= dx || '(N/A)'

          const id = record.id
          tables.diagnostics.ids.push(id)
          tables.diagnostics.records[id] = record
          record.date ||= record.startsAt || record.createdAt
          record.createdAt ||= record.date

          tables.diagnostics.headers.date ||= { items: [] }
          let date = parseDate(record[filters.sortBy] || record.date, '10:00')

          tables.diagnostics.headers.date.items.push({ id, value: date, editable })

          tables.diagnostics.values.label ||= { items: [], id: 'label' }

          let html = `<div style="max-width: 800px; white-space: break-spaces">${label}</div>`

          if (notes && notes !== label) {
            html += `<div style="max-width: 800px; white-space: break-spaces" class="text-secondary">${notes}</div>`
          }

          status = {
            value: status,
            label: t(`ehr:${status}`, { context: 'diagnoses' })
          }

          let dates = []

          try {
            if (startsAt) {
              dates.push(formatDate(startsAt, 'dd-MMM-yyyy').toUpperCase())
            } else {
              dates.push(t('ehr:startsAt_not_defined', 'N/A', { context: 'diagnoses' }))
            }

            if (endsAt) {
              dates.push(formatDate(endsAt, 'dd-MMM-yyyy').toUpperCase())
            } else {
              dates.push(t('ehr:endsAt_not_defined', 'N/A', { context: 'diagnoses' }))
            }
          } catch {}

          status.content = `<div>${status.label}</div>`
          if (status.value === 'intercurrence') {
            status.content = `<div class="text-success">${status.label}</div>`
          } else if (status.value === 'chronic') {
            status.content = `<div class="text-primary">${status.label}</div>`
          } else if (status. value === 'pending') {
            status.content = `<div class="text-warning">${status.label}</div>`
          }

          status.content += `<div class="text-xs text-secondary">${dates.join(' · ')}</div>`
          html = `<div>${status.content}</div><div class="mx-n2 px-2 mt-1">${html}</div>`

          tables.diagnostics.values.label.items.push({ id, value: html })

          // tables.diagnostics.values.status.items.push({ id, value: status.content })
          consultations[group].diagnostics.push({ ...record })
        } else if (record.type === 'labs') {
          const id = record.id
          tables.labs.ids.push(record.id)
          tables.diagnostics.records[id] = record

          record.date ||= record.startsAt || record.createdAt
          record.createdAt ||= record.date

          tables.labs.headers.date ||= { items: [] }
          let date = parseDate(record[filters.sortBy] || record.date, '10:00')

          tables.labs.headers.date.items.push({ id, value: date, editable })

          for (const type of LABS_GROUPS) {
            const names = Object.keys(record[type])
            tables.labs[type] ||= {}
            tables.labs[type].setup ||={ allNumbers: true, orientation: 'vertical' }
            tables.labs[type].values ||= {}
            for (const name of names) {
              // Bug real (auditoría de capturas QA, e2e-test/Fernández Julia
              // y Aguirre Tomás): el modelo asume que `.value` es siempre
              // string (así lo escriben los inputs del form, ver Lab.js),
              // pero un record guardado con el campo en number (ej. carga
              // fuera del form, fixture de test) rompía acá — number no
              // tiene `.trim()`. Se coacciona a string ANTES de trimear,
              // así funciona con cualquier tipo de dato ya persistido.
              let value = String(record[type][name]?.value ?? '').trim()
              if (FALSY.includes(value)) {
                value = '·'
              }

              tables.labs[type].values[name] ||= { items: [], id: name }
              tables.labs[type].values[name].items.push({ id, value })
            }
          }

          if (record.eabrenal?.cr.value) {
            let years
            if (consumer.data.dob) {
              const date = parseDate(record.date || record.startsAt || record.createdAt, '10:00')
              years = Math.round((date.getTime() - new Date(consumer.data.dob).getTime()) / 1000 / 60 / 60 / 24 / 365)
            }
            const clcr = getClCr({
              equation: 'ckd',
              gender: consumer.data.gender,
              weight: false,
              age: years,
              creatinine: record.eabrenal.cr.value,
              unit: record.eabrenal.cr.unit
            })
            let value = clcr?.value
            if (FALSY.includes(value)) {
              value = '·'
            } else {
              record.clcr = value
            }
            tables.labs.eabrenal.values.clcr ||= { items: [], id: 'clcr' }
            tables.labs.eabrenal.values.clcr.items.push({
              id, value, message: clcr?.um, class: clcr?.class,
            })
          }
          consultations[group].labs += 1
        } else if (record.type === 'vitalSigns') {
          const id = record.id
          // Stashear `editable` (ventana 48hs + dueño, ya calculado arriba)
          // directo en el record: Percentiles.js (punto 9 de la card,
          // "corrección de registros") lista estos mismos records para
          // editar sin recalcular la regla ni pasar user/EDITABLE_WINDOW_MS
          // por separado — una sola fuente de verdad de qué es editable.
          record.editable = editable
          tables.vitalSigns.records[id] = record
          tables.vitalSigns.ids.push(id)
          record.date ||= record.startsAt || record.createdAt
          record.createdAt ||= record.date

          tables.vitalSigns.headers.date ||= { items: [] }
          let date = parseDate(record[filters.sortBy] || record.date, '10:00')
          tables.vitalSigns.headers.date.items.push({ id, value: date, editable })

          const { weight, height } = record
          tables.vitalSigns.values.bmi ||= { items: [], id: 'bmi' }
          let bmi = {}
          if (weight && height) {
            bmi = getBMI(height, weight) || {}
          } else {

          }

          tables.vitalSigns.values.bmi.items.push({
            id,
            value: bmi.value ? (bmi.value || 0).toFixed(2) : '·',
            message: bmi.warning || '',
            class: bmi.class || '',
          });

          for (const name of vitalSignsVariables) {
            // Mismo bug que labs arriba: record[name] puede venir guardado
            // como number (ej. peso/altura cargados fuera del form v2, que
            // sí siempre manda string) — coaccionar a string antes de
            // trimear en vez de asumir el tipo.
            let value = String(record[name] ?? '').trim()
            if (FALSY.includes(value)) {
              value = '·'
            } else {
              value = Number(value)
            }

            // label: fallback legible para variables NUEVAS que todavía no
            // tienen traducción en el server de /translates (Table.js hace
            // t(`ehr:${id}`, label || id) — sin esto, headperim se vería
            // como la key cruda hasta que alguien cargue la traducción).
            tables.vitalSigns.values[name] ||= { items: [], id: name, label: name === 'headperim' ? 'Perím. cefálico' : undefined }
            tables.vitalSigns.values[name].items.push({ id, value })
          }
          consultations[group].vitalSigns += 1
        } else if (record.type === 'forms' && record.formID) {
          const formID = record.formID
          // Formulario ya migrado a Custom Forms v2: sus registros se ven (y
          // cuentan) en la sección nueva; acá solo duplicarían en solo-lectura.
          // Los ADJUNTOS sí se siguen acumulando: la sección Archivos es donde el
          // profesional los busca (en la grilla v2 quedan solo como links de texto).
          // Campos ocultos (hide) quedan afuera, igual que en el render legacy de abajo.
          if (migratedLegacyFormIDs.has(formID)) {
            const legacyFields = forms.data.find(f => f.id === formID)?.fields || []
            for (const [fieldID, payload] of Object.entries(record.fields || {})) {
              if (legacyFields.find(f => f.id === fieldID)?.hide) continue
              for (const link of (Array.isArray(payload?.files) ? payload.files : [])) {
                const name = link.replace('https://', '')
                consultations[group].files.push({ link, name, deleted })
                files.push({ ...record, link, name, deleted })
              }
            }
            continue
          }
          record.date ||= record.startsAt || record.createdAt
          record.createdAt ||= record.date
          consultations[group].forms += 1

          tables.forms[record.formID] ||= { values: {}, headers: {}, records: {}, ids: [] }
          tables.forms[record.formID].ids.push(record.id)
          tables.forms[record.formID].records[record.id] = record

          tables.forms[record.formID].headers.date ||= { items: [] }
          let date = parseDate(record[filters.sortBy] || record.date, '10:00')
          tables.forms[record.formID].headers.date.items.push({ id: record.id, value: date, editable })

          let form = tables.forms[record.formID].setup || forms.data.find(f => f.id === formID) || {}
          tables.forms[record.formID].setup ||= { ...form, allNumbers: true }

          if (!Array.isArray(form.fields)) {
            form.fields = []
          }

          const fields = Object.entries(record.fields)
          const formFields = [...form.fields]

          for (const [fieldID, payload] of fields) {
            // number
            // date
            // content
            // checked
            // files
            // diagnoses
            // items
            // treatments

            const fieldConfig = formFields.find(f => f.id === fieldID) || {}
            if (fieldConfig.hide) continue
            // El header mostraba el id crudo del campo cuando no había label. Se
            // resuelve como en la v1: label del form, luego el que traiga el propio
            // registro, luego un nombre por tipo — nunca el uuid.
            const fieldType = fieldConfig.type || payload.type
            const fieldLabel = fieldConfig.label || payload.label
              || (fieldType === 'number' ? t('ehr:form_field_type_number', 'Numérica')
                : fieldType === 'date' ? t('ehr:form_field_type_date', 'Fecha')
                : t('ehr:form_field_generic', 'Campo'))
            tables.forms[record.formID].values[fieldID] ||= {
              items: [],
              id: fieldID,
              label: fieldLabel,
              key: `${record.id}${fieldID}`
            }

            // Preferir el texto plano que el registro ya trae (payload.text) sobre el
            // HTML de rich-text (payload.content): en las tablas legacy dentro de la HCE
            // nueva se ve más limpio y evita renderizar HTML crudo. Fallback a content
            // para registros viejos que no tengan text.
            let value = payload.number || payload.date || payload.text || payload.content || '·'
            if (fieldConfig.type  === 'checkbox') {
              value = payload.checked ? t('checked', 'Si') : t('unchecked', 'No')
            } else if (fieldConfig.type  === 'diagnoses') {
              value = payload.diagnoses?.map(d => (
                `<div class="text-sm">${d.label} <sup class="text-secondary">${d.id.replace('cie10:', '')?.toUpperCase()}</sup></div>`
              ))?.join() || '·'
            } else if (fieldConfig.type  === 'files') {
              payload.files ||= []
              value = ''
              for (const link of payload.files) {
                const name = link.replace('https://', '')
                value += `<div class="text-sm"><a href=${link} target="_blank" rel="noopener noreferrer">${name}</a></div>`
                consultations[group].files.push({ link, name, deleted })
                files.push({
                  ...record,
                  link,
                  name,
                  deleted: !!record.deleted
                })
              }

            } else if (fieldConfig.type  === 'items') {
              value = payload.items?.map(i => i.label)?.join(', ')
            } else if (fieldConfig.type  === 'treatments') {
              value = payload.treatments?.map(t => {
                t.product ||= t.drug
                let html = t.product
                if (t.company) {
                  html = `<div>${t.product} <sup class="text-secondary">${t.company}</sup></div>`
                }
                if (t.presentation) {
                  html += `<div class="text-xs text-secondary">${t.presentation}</div>`
                }
                return `<div class="text-xs" data-tooltip-id="app-tooltip" data-tooltip-content="${t.drug && t.drug !== t.product ? t.drug : ''}">${html}</div>`
              })?.join() || '·'
            }
            let text
            if (payload.content) {
              div.innerHTML = value
              text = div.textContent?.trim()
            }
            if (tables.forms[record.formID].setup.allNumbers && !isNaN(text)) {
              value = +text
            } else {
              tables.forms[record.formID].setup.allNumbers = false
            }

            if (!tables.forms[record.formID].setup.allNumbers && value === '·') {
              value = ''
            }

            tables.forms[record.formID].values[fieldID].items.push({
              id: record.id,
              fieldID,
              value,
            })

          }
        }
      }

      for (const groupKey of Object.keys(consultations)) {
        if (groupKey === 'groups') continue
        const consultation = consultations[groupKey]
        if (consultation.evolutions && consultation.evolutions.length > 0) {
          if ((filters.sort === 'desc' || !filters.sort) && (filters.sortBy === 'date' || !filters.sortBy)) {
            consultation.evolutions.sort((a, b) => {
              const res = b.updatedAt - a.updatedAt
              if (res !== 0) return res
              return b.createdAt - a.createdAt
            })
          }
        }
      }
      consultations.groups = Object.keys(consultations)
      setConsultations(consultations)
      setFiles(files)
      setTables(tables)
      setReady(true)
    }
  // percentilesEnabled en deps: es un boolean derivado (estable — no un
  // objeto de query), y decide si la columna de perímetro cefálico entra a
  // las tablas; sin él, el flag que resuelve después del primer build de
  // tablas no agregaba la columna hasta otro trigger cualquiera.
  }, [records.data, newRecords, forms.data, consumer.data, filters, user.state?.uid, percentilesEnabled])

  useEffect(() => {
    if (event.data?.id) try {
      let status = event.data.status
      let states = event.data.states || []
      let state = states.filter(s => status && s.status === status).sort((a, b) => b.createdAt - a.createdAt)[0]
      let createdAt = state?.createdAt || Date.now()
      let low = new Date(createdAt).getTime()
      let hi
      if (status === "fulfilled") {
        hi = low
        low = states
            .filter((s) => s.status === "active")
            .sort((a, b) => b.createdAt - a.createdAt)[0].createdAt
        low = new Date(low).getTime()
      }
      if (low) setTimer({ low, hi })
    } catch (e) {}

  }, [event.data])

  if (!ready) {
    return (
      <div className="mvh-100-1 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary">
          <span className="visually-hidden">{t("loading")}</span>
        </div>
      </div>
    );
  }

  const main = compact ? undefined : 400
  const notesWdith = compact ? undefined : 400;
  const sidebar = filters.records || filters.files || !!prescriptions?._form
  const canEhrConfig = user.state.isStaff ||
                      (user.team?.permissions && user.team.permissions.teamConfig !== false) ||
                      (user.team?.permissions && user.team.permissions.ehrConfig !== false && user.team?.resourceID);

  // Pills de acceso a features Trial (reubicación 2026-09-01, pedido de Juan
  // Cruz) — se arman ACÁ (donde ya vive todo el estado/flags/refs) y se pasan
  // como elementos ya construidos a Consultations/Form (columnas hermanas),
  // en vez de prop-drillear primitivas (enabled/onClick/feedbackRef/etc.) a
  // esos componentes: evita que Form (que solo sabe crear registros) tenga
  // que conocer odontograma/percentiles, y evita re-escribir los ~28 asserts
  // de EHR.test.js que hoy dependen de este JSX (esos archivos mockean
  // Consultations/Form completos). Colores de texto confirmados contra el
  // Figma real (fileKey wRHI7hLbh9o3I72gKTSqVx, nodo 3303:1596): #13629b
  // para Resumen IA y Escriba, #409eff para Odontograma y Percentiles — la
  // forma (bg blanco, rounded-3, padding 8px) es la misma para las 4.
  const hceSummaryTrialTool = hceSummaryPlanLocked ? (
    <PlanLockedTool featureKey="hce.ai_summary" label={t('ehr:hce_summary_create_button', 'Crear resumen con IA')} color="#13629b" onClick={hceSummaryLock.open} />
  ) : hceSummaryEnabled && (
    <div
      className="d-flex align-items-center gap-1 rounded-3"
      style={{ padding: 4, backgroundColor: showHceSummary ? '#e9ecef' : '#fff' }}
    >
      <button
        data-testid="hce-summary-toggle-button"
        className="d-flex align-items-center btn p-0 border-0 font-nunito"
        style={{ background: 'transparent', color: '#13629b', opacity: hceSummaryTermsLocked ? 0.5 : 1 }}
        onClick={hceSummaryTermsLocked ? () => setShowTermsLock(true) : () => { setShowHceSummary(!showHceSummary); setShowNotes(false) }}
      >
        <div>
          {t('ehr:hce_summary_create_button', 'Crear resumen con IA')}
        </div>
      </button>
      {/* Hermano del botón, NO anidado adentro — un <button> (el ícono info
          de TrialInfoPopover) dentro de OTRO <button> es HTML inválido y
          hace que clickearlo también dispare el onClick del botón
          contenedor por bubbling (abriría/cerraría el panel Y el popup a la
          vez). */}
      <TrialInfoPopover
        title={t('ehr:hce_summary_feature_name', 'Resumen de HCE con IA')}
        description={t('ehr:hce_summary_trial_description', 'Generá un resumen automático de la historia clínica del paciente a partir de sus evoluciones. Te ahorra tiempo de lectura antes de una consulta.')}
        onLeaveReview={() => hceSummaryFeedbackRef.current?.openManual()}
        align="left"
      />
    </div>
  )

  // disabled={showPercentiles}, NO dejar de renderizar (feedback de QA
  // 2026-09-02: el botón desaparecía de la barra al abrir la OTRA feature,
  // efecto confuso). El hallazgo de la pasada 6 del loop que motivó ocultar
  // este trigger sigue vigente y hay que preservarlo: ninguno de los
  // modales full-screen de esta pantalla implementa un focus-trap real
  // (solo foco inicial + ESC), así que un usuario de teclado podía Shift+Tab
  // desde DENTRO del odontograma, llegar de nuevo a este trigger (seguía
  // montado detrás del backdrop) y abrir un SEGUNDO modal full-screen
  // superpuesto. `disabled` en vez de no-renderizar logra lo mismo (un
  // <button disabled> sale del tab order Y no dispara click) sin la
  // confusión de un botón que se esfuma — sigue visible, sólo inerte.
  const odontogramaTrialTool = odontogramaPlanLocked ? (
    <PlanLockedTool featureKey="hce.odontograma" label={t('ehr:odontograma_create_button', '+Odontograma')} color="#409eff" onClick={odontogramaLock.open} />
  ) : odontogramaEnabled && (
    <div className="d-flex align-items-center gap-1 rounded-3 bg-white" style={{ padding: 4 }}>
      <button
        data-testid="odontograma-toggle-button"
        className="d-flex align-items-center btn p-0 border-0 font-nunito"
        style={{ color: '#409eff', opacity: odontogramaTermsLocked ? 0.5 : 1 }}
        onClick={odontogramaTermsLocked ? () => setShowTermsLock(true) : () => setShowOdontograma(true)}
        disabled={showPercentiles}
      >
        <div>{t('ehr:odontograma_create_button', '+Odontograma')}</div>
      </button>
      {/* Card Odontograma V2 (feedback de UX): el popup de info DENTRO del
          modal quedaba mal posicionado (encabezado en columna, distinto del
          resto de las features trial) — se sacó de ahí y queda solo acá,
          junto al botón que abre el modal. Con el modal abierto esta barra
          no es alcanzable de todos modos (z-index 1050 < 1055), así que no
          hace falta un segundo trigger adentro. */}
      <TrialInfoPopover
        title={t('ehr:odontograma_feature_name', 'Odontograma')}
        description={t('ehr:odontograma_trial_description', 'Registrá el estado dental del paciente en un gráfico interactivo. Podés marcar piezas, condiciones y tratamientos, y el registro queda en la HCE.')}
        onLeaveReview={() => odontogramaFeedbackRef.current?.openManual()}
      />
    </div>
  )

  // disabled={showOdontograma}: mismo motivo y mismo criterio que el gate
  // de Odontograma más arriba (pasada 6 del loop, evita el par de modales
  // full-screen apilados por teclado) — visible siempre, inerte mientras la
  // otra feature está abierta.
  const percentilesTrialTool = percentilesPlanLocked && percentilesApplicable ? (
    <PlanLockedTool featureKey="hce.percentiles" label={t('ehr:percentiles_create_button', '+Percentiles')} color="#409eff" onClick={percentilesLock.open} />
  ) : percentilesEnabled && percentilesApplicable && (
    <div className="d-flex align-items-center gap-1 rounded-3 bg-white" style={{ padding: 4 }}>
      <button
        data-testid="percentiles-toggle-button"
        className="d-flex align-items-center btn p-0 border-0 font-nunito"
        style={{ color: '#409eff', opacity: percentilesTermsLocked ? 0.5 : 1 }}
        onClick={percentilesTermsLocked ? () => setShowTermsLock(true) : () => setShowPercentiles(true)}
        disabled={showOdontograma}
      >
        <div>{t('ehr:percentiles_create_button', '+Percentiles')}</div>
        {/* Cruce de ≥2 carriles de percentil detectado (server): el detalle
            está dentro del modal — esto solo avisa que HAY algo que mirar,
            sin exponer el dato en la toolbar. */}
        {percentilesAlertCount > 0 && (
          <span
            className="badge rounded-pill bg-danger"
            data-testid="percentiles-alert-badge"
            title={t('ehr:percentiles_alert_badge_hint', 'Cruce de carriles de percentil detectado — abrí el gráfico')}
          >
            <span aria-hidden="true">!</span>
            {/* El title de un span no-focuseable no lo anuncian los
                lectores de pantalla: sin esto, el nombre accesible del botón
                era "Percentiles !". */}
            <span className="visually-hidden">{t('ehr:percentiles_alert_badge_hint', 'Cruce de carriles de percentil detectado — abrí el gráfico')}</span>
          </span>
        )}
      </button>
      <TrialInfoPopover
        title={t('ehr:percentiles_feature_name', 'Curvas de percentiles')}
        description={t('ehr:percentiles_trial_description', 'Graficá el crecimiento del paciente comparado con referencias poblacionales. Especialmente útil en pediatría para seguir talla, peso y perímetro cefálico a lo largo del tiempo.')}
        onLeaveReview={() => percentilesFeedbackRef.current?.openManual()}
      />
    </div>
  )

  return (
    <div className={styles.container}>
      <Consumer consumer={consumer.data} />

      {/* Fix responsive (2026-08-14, reportado por Nacho): esta fila lleva
          flex-wrap. Al entrar por la videollamada (que además suma el botón
          "Finalizar" con el timer) esta fila de ~10 filtros/acciones sin
          wrap no entraba en un celular y forzaba overflow horizontal de TODA
          la página — no solo de esta barra. Con la página más ancha que el
          viewport, el Room (position:fixed) queda mal ubicado/"scrolleando"
          en vez de fijo, porque deja de haber un viewport real sin scroll
          horizontal contra el cual fijarse.

          Las pills de features Trial (Resumen IA/Odontograma/Percentiles/
          Escriba) YA NO viven acá (pedido de Juan Cruz, reubicación
          2026-09-01) — Resumen IA se pasa como `trialTools` a Consultations
          (columna izquierda, debajo del buscador), Odontograma/Percentiles/
          Escriba se pasan a Form (barra de creación de registros, columna
          derecha) — Odontograma/Percentiles como `trialTools`, Escriba como
          `trialToolsEnd` (último ítem de esa misma fila, no un prop
          separado por capricho: necesita `recording`/`feedbackRef`, que
          `trialTools` no carga). Ver el render de ambos componentes más
          abajo, y el de Escriba (modal + `trialToolsEnd`) junto al de Form. */}
      <div className="bg-white mt-1 shadow-sm px-2 py-1">
      <div className="d-flex flex-wrap align-items-center gap-2">
      {/* Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04):
          flex-wrap del renglón entero bajaba de línea con muchos filtros
          activos (labs + formularios + recetas), dando una altura de
          toolbar variable según el paciente. Se agrupan los checkboxes de
          sección en su propio sub-contenedor con scroll lateral.
          overflow-x-auto (Bootstrap) es un no-op en 5.2.0 — no existe hasta
          5.3 — así que el clip/scroll real lo hacía overflowX inline
          (hallazgo 2026-09-08).

          Bug real (seguimiento 2026-09-09, Juan Cruz QA): ese sub-
          contenedor (flex-nowrap + overflow-x:auto + flex-basis chico)
          aplicaba en TODOS los viewports — en desktop, donde los
          checkboxes entran de sobra en una sola línea sin necesitar wrap
          ni scroll (el flex-wrap del renglón exterior ya alcanzaba, como
          antes de cualquiera de estos fixes), apretaba los checkboxes en
          una caja angosta y forzaba un scroll innecesario que tapaba
          contenido. styles.checkboxesRow (ver styles.module.scss) mueve
          ese comportamiento a un @media real, solo por debajo de sm — en
          desktop es display:contents (el div desaparece de la caja de
          layout, sus hijos son directos del flex-wrap exterior, como si
          este wrapper no existiera). Las acciones de la derecha
          (Visibilidad/Imprimir/Filters/badge de Escriba) siguen alineadas
          al margen derecho por el spacer flex-fill de siempre. */}
      <div className={styles.checkboxesRow}>
        {timer && (
          <button onClick={endConsultation} className="btn btn-sm text-pink bg-light fw-bold text-truncate border">
            {event.data.status === 'active' ? (
              t('ehr:end_active_consultation', 'Finalizar')
            ) : (
              t('ehr:end_consultation', 'Finalizar')
            )} - <span style={{ fontFamily: 'monospace' }}><Timer low={timer.low} hi={timer.hi} /></span>
          </button>
        )}

        {['records', 'files', 'diagnostics', 'treatments', 'vitalSigns', 'labs'].filter(type => stats[type] > 0 || !['labs'].includes(type)).map(type => (
          <button key={type} onClick={() => setFilters({ ...filters, [type]: !filters[type] })} className="btn btn-sm btn-link p-2 d-flex align-items-center gap-1">
            {filters[type] ? (
              <IconCheckSquare width="13" height="13" className="text-dark" />
            ) : (
              <IconSquare style={{ opacity: 0.35 }} width="13" height="13" className="text-dark" />
            )}{' '}{t(`ehr:${type}`, type)}<sup data-testid={`filter-count-${type}`} className="text-secondary">{stats[type] || 0}</sup>
          </button>
        ))}
        <button
          onClick={() => setFilters({ ...filters, customForms: !filters.customForms })}
          className="btn btn-sm btn-link p-2 d-flex align-items-center gap-1"
        >
          {filters.customForms ? (
            <IconCheckSquare width="13" height="13" className="text-dark" />
          ) : (
            <IconSquare style={{ opacity: 0.35 }} width="13" height="13" className="text-dark" />
          )}{' '}{t('ehr:customForms', 'Formularios')}<sup className="text-secondary">{(customFormRecords.data?.length || 0) + (stats.forms || 0)}</sup>
        </button>
        <button onClick={() => setPrescriptions({ ...prescriptions, _form: !prescriptions._form })} className="btn btn-sm btn-link p-2 d-flex align-items-center gap-1">
          {prescriptions?._form ? (
            <IconCheckSquare width="13" height="13" className="text-dark" />
          ) : (
            <IconSquare style={{ opacity: 0.35 }} width="13" height="13" className="text-dark" />
          )}{' '}{t('ehr:prescriptions', 'Recetas')}<sup className="text-secondary">{prescriptions?.docs?.length || 0}</sup>
        </button>
      </div>
        <div className="flex-fill" />
        {canEhrConfig && (
         <button onClick={handleOpenVisibilityModal} className="btn btn-sm btn-link px-2 py-1 d-flex align-items-center gap-1">
           <IconEye className="text-secondary" /> {t('ehr:visibility_config', 'Visibilidad HCE')}
         </button>
        )}
        <button onClick={onPrintClick} disabled={printBusy} className="btn btn-sm btn-link px-2 py-1 d-flex align-items-center gap-1">
          <IconPrinter className="text-secondary" /> {printBusy ? t('ehr:print_preparing', 'Preparando...') : t('print', 'Imprimir')}
        </button>
        <Filters filters={filters} onChange={payload => setFilters({ ...filters, ...payload })}/>
        {/* Badge de pendientes/fallidas de Escriba + reintentar (hallazgo del
            loop de hardening, F5: sin esto una consulta que falla en STT/IA
            no deja NINGÚN rastro visible — "nunca fallo silencioso", §7.2).
            Vive ACÁ (no pegado al botón de Escriba en la toolbar de Form,
            reubicación 2026-09-01) porque su dropdown es position:absolute
            anclado a este contenedor — dentro de la columna overflow-auto de
            Form se recortaría (mismo tipo de bug ya sufrido por
            TrialInfoPopover en prod el 28/8). Retorna null sin nada
            pendiente, así que no deja una franja vacía. */}
        {escribaEnabled && <EscribaPending api={escribaApi} consumerID={consumerID} t={t} />}
        <button
          data-testid="notes-toggle-button"
          className="d-flex gap-2 align-items-center border-light btn font-nunito ms-3"
          onClick={() => { setShowNotes(!showNotes); setShowHceSummary(false) }}
          style={{
            borderWidth: '2px',
            padding: '4px 8px',
            ...showNotes ? { backgroundColor: '#e9ecef' } : {},
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-file-earmark-text" viewBox="0 0 16 16">
            <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5"/>
            <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"/>
          </svg>
          <div>
            {t('ehr:notes', 'Notas')}
          </div>
        </button>
      </div>
      </div>

      {hceSummaryEnabled && (
        <BetaFeedback
          ref={hceSummaryFeedbackRef}
          featureKey="hce_summary"
          featureName={t('ehr:hce_summary_feature_name', 'Resumen de HCE con IA')}
        />
      )}

      {/* Feedback del odontograma desde la TOOLBAR — único punto de entrada
          (ver comentario junto al TrialInfoPopover, más arriba). Montado
          solo con el modal cerrado: con el modal abierto este form quedaría
          detrás (zIndex 1050 vs 1055 del modal) y el badge que lo abre no es
          alcanzable, pero tampoco hace falta: la barra en sí no es
          alcanzable con el modal abierto. */}
      {odontogramaEnabled && !showOdontograma && (
        <BetaFeedback
          ref={odontogramaFeedbackRef}
          featureKey="odontograma"
          featureName={t('ehr:odontograma_feature_name', 'Odontograma')}
        />
      )}

      {/* Mismo criterio que el odontograma: el form de la toolbar solo vive
          con el modal cerrado (zIndex 1050 < 1055); dentro del modal hay otro
          badge con su propio form. */}
      {percentilesEnabled && percentilesApplicable && !showPercentiles && (
        <BetaFeedback
          ref={percentilesFeedbackRef}
          featureKey="percentiles"
          featureName={t('ehr:percentiles_feature_name', 'Curvas de percentiles')}
        />
      )}

      {/* Mismo criterio de zIndex que odontograma/percentiles: el form de acá
          sólo vive con el modal de pre-inicio cerrado — con la barra de
          grabación (no es un modal, zIndex 10) no hay conflicto, así que el
          gate es sólo contra `showEscriba`. */}
      {escribaEnabled && !showEscriba && (
        <BetaFeedback
          ref={escribaFeedbackRef}
          featureKey="escriba"
          featureName={t('ehr:escriba_feature_name', 'Grabar consulta')}
        />
      )}

      {resource.data?.id && resource.data.createdAt <= V2_DATE && closedAt && closedAt + V2_CLOSE <= Date.now() && (resource.data.metadata?.ehr?.version !== 'v2:hard' || location.search.includes('mode=static')) && (
        <div className="px-2 py-1 bg-warning shadow-sm d-flex align-items-center">
          <small className="flex-fill">{t('ehr:v2_back_v1_or_default_message', 'Podes usar esta version de manera predeterminada o volver a la version anterior')}</small>
          <div className="d-flex align-items-center gap-1">
            <button onClick={setDefault} className="btn btn-sm btn-link">{t('ehr:v2_set_to_default', 'Usar')}</button>
            <Link className="btn btn-sm btn-link" to={`${location.pathname.replace('/ehr/v2', '/ehr')}${location.search}`}>{t('ehr:v2_back_v1_btn', 'Volver')}</Link>
            <button onClick={closeV2Message} className="btn btn-sm">{t('ehr:v2_back_v1_close', 'Cerrar')}</button>
          </div>
        </div>
      )}

      <div className="p-3">
        <div
          data-testid="ehr-layout-row"
          className={`d-flex d-print-block gap-3 ${styles.printAutoHW} ${compact ? 'flex-column' : ''}`}
          style={compact ? undefined : { height: `calc(100vh - 220px)` }}
        >
          {sidebar && (
            <div
              style={{ width: main, maxWidth: main, minWidth: main, }}
              className={`${compact ? '' : 'h-100'} ${styles.printAutoHW} ${main ? '' : 'flex-fill'}`}
            >
               <Consultations
                 user={user}
                 consultations={consultations}
                 files={files}
                 resource={resource.data}
                 resourceEhrConfig={originalResourceEhrConfig}
                 filters={filters}
                 prescriptions={prescriptions}
                 onRecordUpdate={onRecordUpdate}
                 aiSummaryPending={aiSummaryPending}
                 aiSummaryOutcome={aiSummaryOutcome}
                 trialTools={hceSummaryTrialTool}
               />
            </div>
          )}
          <div
            className={`flex-fill overflow-auto ${styles.printAutoHW}`}
            style={{ width: compact ? '100%' : `calc(100% ${sidebar ? `- ${main}px` : ''} ${(showNotes || showHceSummary) ? `- ${notesWdith}px` : ''} - 1rem)` }}
          >
            {user.state?.uid && consumer.data?.id && (
              <Form
                key={consumerID}
                tabs
                labs={false}
                forms={false}
                consumer={consumer.data}
                resource={resource.data}
                user={user.state}
                trialTools={<>{odontogramaTrialTool}{percentilesTrialTool}</>}
                trialToolsEnd={escribaPlanLocked ? (
                  <PlanLockedTool featureKey="escriba.recording" label={t('ehr:escriba_button', 'Grabar consulta')} color="#13629b" onClick={escribaLock.open} className="shadow" />
                ) : escribaEnabled && (
                  <EscribaButton
                    onClick={escribaTermsLocked ? () => setShowTermsLock(true) : () => setShowEscriba(true)}
                    locked={escribaTermsLocked}
                    feedbackRef={escribaFeedbackRef}
                    t={t}
                  />
                )}
                onSave={(records, flag) => {
                  // Las recetas/órdenes se refrescan por su propia query y no
                  // aportan un record a newRecords (el arg llega undefined), así
                  // que cortamos acá antes del loop que dereferencia record.id.
                  if (flag === 'prescriptions:update') {
                    qPrescriptions.refetch()
                    return
                  }
                  if (!records || !Array.isArray(records)) return
                  // Screening pasivo de percentiles: una medición recién
                  // cargada puede cruzar carriles — sin este refetch, el
                  // badge de alerta del botón Percentiles quedaba congelado
                  // al estado de cuando se abrió la HCE (la query tiene
                  // staleTime y refetchOnWindowFocus está deshabilitado
                  // globalmente). Solo si la query está activa (flag del
                  // team + paciente pediátrico) — refetch() sobre una query
                  // disabled la ejecutaría igual. DEMORADO 8s: ver el
                  // comentario de growthRefetchTimer (el write path del
                  // backend es asíncrono; un refetch inmediato trae y
                  // "refresca" los datos PRE-guardado).
                  if (percentilesEnabled && isPediatric && records.some(record => record.type === 'vitalSigns')) {
                    clearTimeout(growthRefetchTimer.current)
                    growthRefetchTimer.current = setTimeout(() => growthRefetchRef.current(), 8000)
                  }
                  const array = [...newRecords]
                  for (const record of records) {
                    let index = array.findIndex(r => r.id === record.id)
                    if (index >= 0) {
                      array[index] = { ...record }
                    } else {
                      array.push({ ...record })
                    }
                  }
                  addRecords(array)
                }}
              />
            )}
            {filters.customForms && (
              <div className="mb-3">
                <CustomForms
                  consumer={consumer.data}
                  resource={resource.data}
                  user={user.state}
                />
                {/* Formularios de la versión anterior (solo lectura): integrados al final
                    de la sección nueva, bajo el mismo checkbox de visibilidad. La migración
                    a v2 la dispara el equipo por-team desde Igor, no el profesional. */}
                {Object.keys(tables?.forms || {}).length > 0 && (
                  <div className="d-flex align-items-center gap-2 border-bottom bg-white p-2 mt-3">
                    <h4 className="text-black text-first-up fw-normal m-0">
                      {t('ehr:legacy_forms', 'Formularios (versión anterior)')}
                    </h4>
                  </div>
                )}
                {Object.entries(tables?.forms || {}).map(([formID, entries]) => (
                  <div key={formID} className="bg-white shadow-sm mb-3">
                    <Table data={entries} label={entries.setup.label} />
                  </div>
                ))}
              </div>
            )}
            <Tables
              filters={filters}
              tables={tables}
              onSave={record => {
                const array = [...newRecords]
                let index = array.findIndex(r => r.id === record.id)
                if (index >= 0) {
                  array[index] = { ...record }
                } else {
                  array.push({ ...record })
                }
                addRecords(array)
              }}
            />
          </div>

          {!!showNotes && (
            <div className="d-print-none" style={{ width: notesWdith ? `${notesWdith}px` : '100%' }}>
              <Notes consumer={consumer.data} resource={resource.data} onClose={() => setShowNotes(false)} />
            </div>
          )}

          {!!showHceSummary && (
            <div className="d-print-none" style={{ width: notesWdith ? `${notesWdith}px` : '100%' }}>
              {/* key={consumerID}: bug real (pasada de revisión 2026-08-11) —
                  la ruta reusa esta MISMA instancia de EHR al cambiar de
                  paciente (mismo path pattern, @reach/router no remonta por
                  cambio de param), así que sin esto el estado interno de
                  useHceSummaryApi (generating/generateError) y el rating ya
                  seleccionado en HceSummaryRating quedaban pegados del
                  paciente anterior. */}
              {/* `enabled` se pasa a propósito: este panel sólo se monta desde
                  el botón de la toolbar, que existe porque `useFeatureAccess` ya
                  resolvió el acceso en un único /access. Sin esta prop el hook
                  volvía a pedir /hce-summary/enabled y la búsqueda de resúmenes
                  quedaba esperando ese round-trip redundante (feedback de
                  performance de la reunión del 19/08). */}
              <HceSummary
                key={consumerID}
                consumerID={consumerID}
                enabled={hceSummaryEnabled}
                onClose={() => setShowHceSummary(false)}
              />
            </div>
          )}
        </div>
      </div>

      {odontogramaEnabled && showOdontograma && (
        <Suspense fallback={null}>
          {/* key={consumerID}: la ruta reusa esta MISMA instancia de EHR al
              cambiar de paciente (@reach/router no remonta por cambio de
              param) — sin la key, el buffer de pendientes y la selección
              quedarían pegados del paciente anterior (mismo bug real ya
              documentado en el panel de HceSummary de abajo). */}
          <Odontograma
            key={consumerID}
            consumerID={consumerID}
            consumer={consumer.data}
            // El flag de ARCA viene del mismo request de features: el modal no
            // hace su propia consulta para saber si mostrar "Facturar".
            arcaEnabled={betaFeatures.arca}
            // Sub-flag del odontograma (checkbox anidado en Igor): viaja en el
            // mismo request de features, ya cruzado con el flag padre.
            voiceEnabled={odontogramaVoiceEnabled}
            // Emisor del presupuesto: un papel que el paciente se lleva sin
            // nombre del consultorio ni del profesional es un papel anónimo.
            // Ambos salen de datos que esta pantalla YA tiene cargados.
            teamLabel={user.team?.label}
            professionalLabel={resource.data?.label}
            onClose={() => setShowOdontograma(false)}
          />
        </Suspense>
      )}

      {percentilesEnabled && percentilesApplicable && showPercentiles && (
        <Suspense fallback={null}>
          {/* key={consumerID}: mismo motivo que el odontograma — @reach/router
              reusa esta instancia de EHR al cambiar de paciente y sin la key
              la query/estado del modal quedarían del paciente anterior. */}
          <Percentiles
            key={consumerID}
            consumerID={consumerID}
            consumer={consumer.data}
            resource={resource.data}
            user={user.state}
            vitalSignsRecords={tables?.vitalSigns?.records ? Object.values(tables.vitalSigns.records) : []}
            onClose={() => setShowPercentiles(false)}
            onRecordUpdate={onRecordUpdate}
          />
        </Suspense>
      )}

      {/* Secciones de Odontograma+Percentiles para "Imprimir HCE" — recién se
          monta (una vez, para siempre) después del primer click en
          "Imprimir" que las necesitó (ver onPrintClick). d-none/d-print-block
          vive DENTRO de PrintExtras — acá solo se controla si el chunk+las
          queries on-demand existen o no. */}
      {printExtrasReady && (
        <Suspense fallback={null}>
          <PrintExtras
            growthData={growthForAlerts.data}
            odontogramaChart={odontogramaChartForPrint.data}
            odontogramaCatalog={odontogramaCatalogForPrint.data}
            t={t}
          />
        </Suspense>
      )}

      {/* A diferencia de Odontograma/Percentiles: SIEMPRE montado mientras
          `escribaEnabled` (no gateado por `showEscriba`) y SIN `key={consumerID}`
          a propósito — Escriba.js decide sola si mostrar el modal de
          pre-inicio o la barra persistente según su propio estado interno de
          grabación. Un `key` por paciente forzaría un remount de este
          subárbol al cambiar de paciente (mismo bug que justifica el key en
          los otros dos modales), pero acá el efecto sería el opuesto al
          buscado: el hook de grabación no tiene un cleanup de unmount que
          suelte el micrófono, así que un remount a mitad de una grabación la
          dejaría orfanada (mic prendido, sin barra para cortarla). Por eso
          `useWarnUnsavedChanges` intercepta la navegación ANTES de llegar a
          este punto en vez de confiar en que el remount se banque solo — y
          por eso ese hueco (unmount sin pasar por el guard) queda anotado
          para el loop de hardening (F5), no resuelto acá. */}
      {escribaEnabled && (
        <Suspense fallback={null}>
          <Escriba
            open={showEscriba}
            onOpenChange={setShowEscriba}
            consumerID={consumerID}
            consumer={consumer.data}
            eventID={eventID}
            t={t}
          />
        </Suspense>
      )}

      {showVisibilityModal && (
        <EHRVisibilityModal
          onHide={() => setShowVisibilityModal(false)}
          visibility={resourceEhrConfig?.visibility}
          onChange={handleVisibilityChange}
          onSave={saveEhrConfig}
          type="resource"
          resource={resource.data}
        />
      )}
      <TermsLockModal show={showTermsLock} onClose={() => setShowTermsLock(false)} />
      {hceSummaryLock.modal}
      {odontogramaLock.modal}
      {percentilesLock.modal}
      {escribaLock.modal}
    </div>
  );
}

export default EHR;
